import api from "../api/api";

import store, { history } from "../store";

import { setAlert } from "./alert";
import { getCroppedImg } from "../utils/image";

//Redux types
import {
  GET_HOME_FEED,
  HOME_FEED_ERROR,
  FETCH_FEED_POST,
  SET_FEED_POST,
  SET_HOME_FEED,
  GET_FEED_POST_ERROR,
  REMOVE_INVALID_POST,
  GET_POST_COMMENTS,
  GET_POST_COMMENTS_RESET,
  GET_POST_COMMENTS_ERROR,
  SET_POST_INTERACTIONS,
  FEED_DELETE_COMMENT,
  COMMENT_LIKE,
  COMMENT_LIKE_ERROR,
  REFRESH_HOME_FEED,
  //Dual Feed System
  SET_FEED_TYPE,
  GET_FOLLOWING_FEED,
  GET_TRENDING_FEED,
  REFRESH_FOLLOWING_FEED,
  REFRESH_TRENDING_FEED,
  SET_FOLLOWING_FEED,
  SET_TRENDING_FEED,
  FOLLOWING_FEED_ERROR,
  TRENDING_FEED_ERROR,
  UPDATE_POST_CACHE_INTERACTIONS,
} from "./types";

export const getHomeFeed = () => async (dispatch) => {
  try {
    //Reset loading, and error values
    dispatch({
      type: GET_HOME_FEED,
    });
    //Get state
    const state = store.getState();
    const feedState = state.feed;

    /*If feed already loaded, don't make another call
    if (feedState.isEndOfFeed) {
      console.log("End of feed");
      return;
    }*/

    const res = await api.get(`/feed/posts/${feedState.offset}`);

    const newPosts = feedState.posts ? [...feedState.posts, ...res.data.posts] : res.data.posts;

    const newOffset = feedState.offset + res.data.numPosts;

    const payloadData = {
      posts: newPosts,
      offset: newOffset,
      isEndOfFeed: res.data.isEndOfFeed,
      numPosts: res.data.numPosts,
    };

    dispatch({
      type: SET_HOME_FEED,
      payload: payloadData,
    });
  } catch (error) {
    dispatch({
      type: HOME_FEED_ERROR,
    });
  }
};

export const refreshHomeFeed = () => async (dispatch) => {
  try {
    dispatch({
      type: REFRESH_HOME_FEED,
    });

    const res = await api.get(`/feed/posts/0`);

    const payloadData = {
      posts: res.data.posts,
      offset: res.data.numPosts,
      isEndOfFeed: res.data.isEndOfFeed,
      numPosts: res.data.numPosts,
    };

    dispatch({
      type: SET_HOME_FEED,
      payload: payloadData,
    });
  } catch (error) {
    dispatch({
      type: HOME_FEED_ERROR,
    });
  }
};

export const updatePostCacheInteractions = (postId, interactionUpdates) => (dispatch) => {
  dispatch({
    type: UPDATE_POST_CACHE_INTERACTIONS,
    payload: {
      postId,
      interactionUpdates,
    },
  });
};

export const getPostData =
  (postid, index, repostuser = null) =>
  async (dispatch) => {
    try {
      // Check cache first to avoid refetch
      const state = store.getState();
      const cachedPost = state?.feed?.postCache?.byId?.[postid];
      const errorCooldown = state?.feed?.postCache?.errors?.[postid];

      // Skip if post is in error cooldown period (2 minutes)
      if (errorCooldown && errorCooldown > Date.now()) {
        return;
      }

      if (cachedPost && cachedPost.expiry > Date.now()) {
        // Cache is valid, compose response
        const cachedRepost = repostuser ? cachedPost.reposts?.[repostuser] : null;
        const composed = cachedRepost ? { ...cachedPost.data, repost: cachedRepost } : cachedPost.data;

        dispatch({
          type: SET_FEED_POST,
          payload: {
            index,
            data: composed,
          },
        });
        return;
      }

      // Dispatch loading start
      dispatch({
        type: FETCH_FEED_POST,
        payload: { index, postid },
      });

      // Fallback to network
      const params = new URLSearchParams({ postid });
      if (repostuser) params.append("repostuser", repostuser);
      const res = await api.get(`/post/feed?${params.toString()}`, { timeout: 30000 }); //Extend to 30s timeout, 20 calls at once, allow for mobile

      dispatch({
        type: SET_FEED_POST,
        payload: {
          index: index,
          data: res.data,
        },
      });
    } catch (error) {
      // Determine if this is a permanent error that should trigger cleanup
      const isPermanentError =
        error.response?.status === 404 &&
        (error.response?.data?.msg === "Post not found" ||
          error.response?.data?.msg === "Recipe not found for post" ||
          error.response?.data?.msg === "User not found for recipe" ||
          error.response?.data?.msg === "User not found for post");

      dispatch({
        type: GET_FEED_POST_ERROR,
        payload: {
          index,
          postid,
          isPermanentError, // Flag for cleanup consideration
        },
      });

      // If this is a permanent error, remove immediately
      if (isPermanentError) {
        dispatch({
          type: REMOVE_INVALID_POST,
          payload: { postid, index },
        });
      }
    }
  };

export const getCommentsForPost = (postid) => async (dispatch) => {
  try {
    const res = await api.get(`/post/comments/${postid}`);

    dispatch({
      type: GET_POST_COMMENTS,
      payload: res.data.comments,
    });
  } catch (error) {
    dispatch({
      type: GET_POST_COMMENTS_ERROR,
    });
    console.log(error);
  }
};

export const closePostComments = () => (dispatch) => {
  dispatch({
    type: GET_POST_COMMENTS_RESET,
  });
};

export const commentOnPost = (postid, comment) => async (dispatch) => {
  try {
    const body = {
      text: comment,
    };
    const res = await api.post(`/post/comment/${postid}`, body);

    console.log(res.data);

    //Add my comment to the top of comments
    const state = store.getState();

    const singleComment = {
      _id: res.data.comment._id,
      text: comment,
      user: {
        _id: state.auth.user._id,
        username: state.auth.user.username,
        picture: state.profile.currentProfile.picture,
      },
      date: Date.now(),
      likes: [],
      replies: [],
    };

    const newComments = [singleComment, ...state.feed.comments];

    dispatch({
      type: GET_POST_COMMENTS,
      payload: newComments,
    });

    // Update post cache to reflect new comment count and hasCommented status
    dispatch(
      updatePostCacheInteractions(postid, {
        commentsCount: (state.feed.postCache?.byId?.[postid]?.data?.interactions?.commentsCount || 0) + 1,
        hasCommented: true,
      })
    );

    return true;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export const createPost =
  ({ postText, videoUrl }) =>
  async (dispatch) => {
    try {
      //Form data
      const formData = new FormData();

      //Append cropped images
      const state = store.getState();

      const images = state.media.images;

      if (postText.trim() === "" && images.length === 0 && !videoUrl) {
        dispatch(setAlert("Post cannot be empty", "error"));
        return;
      }

      let postData = {
        type: "text",
        text: postText.trim(),
      };

      if (images.length > 0) {
        postData.type = "media";
        for (let i = 0; i < images.length; i++) {
          const image = images[i];

          if (image.cropped) {
            const croppedImage = await getCroppedImg(image.previewUrl, image.cropped.croppedAreaPixels);

            //Convert dataURL to Blob
            const response = await fetch(croppedImage);
            const blob = await response.blob();
            formData.append("images", blob, `image-${i}.jpg`);
          }
        }
      } else if (videoUrl && videoUrl.trim() !== "") {
        if (videoUrl.trim().length < 256) {
          postData.type = "video";
          postData.videoUrl = videoUrl;
        } else {
          dispatch(setAlert("Video URL is too long", "error"));
          return;
        }
      }

      //Append recipe data as JSON object
      formData.append("post", JSON.stringify(postData));

      // Send the form data to your backend route
      try {
        const response = await api.post("/post/create/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        dispatch(setAlert("Post created", "success"));
        return true;
      } catch (error) {
        // Handle the error
        console.error(error);

        dispatch(setAlert("Error creating post. Refresh and try again.", "error"));
        return false;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  };

export const setPostInteractions = (postid, interactions) => async (dispatch) => {
  dispatch({
    type: SET_POST_INTERACTIONS,
    payload: {
      postid,
      interactions,
    },
  });
};

export const deleteComment = (postid, commentid) => async (dispatch) => {
  //TO DO
  //Send delete request
  try {
    const res = await api.delete(`/post/comment/${postid}/${commentid}`);

    dispatch({
      type: FEED_DELETE_COMMENT,
      payload: {
        postid,
        commentid,
      },
    });

    dispatch(getCommentsForPost(postid));

    // Update post cache to reflect decreased comment count and check if user still has comments
    const state = store.getState();
    const currentCount = state.feed.postCache?.byId?.[postid]?.data?.interactions?.commentsCount || 0;
    const currentUserId = state.auth.user?._id;

    // Check if current user still has any comments after deletion
    const remainingComments = state.feed.comments.filter(
      (comment) => comment._id !== commentid && comment.user._id === currentUserId
    );
    const hasCommented = remainingComments.length > 0;

    dispatch(
      updatePostCacheInteractions(postid, {
        commentsCount: Math.max(0, currentCount - 1),
        hasCommented,
      })
    );
  } catch (error) {
    console.log(error);
    dispatch(setAlert("Could not delete comment. Please refresh the page and try again", "error"));
  }
};

export const likeComment = (postid, commentid) => async (dispatch, getState) => {
  // Get current state before optimistic update
  const state = getState();
  const currentComment = state.feed.comments.find((c) => c._id === commentid);
  const previousState = currentComment
    ? {
        hasLiked: currentComment.hasLiked,
        likesCount: currentComment.likes.length,
      }
    : null;

  // Optimistic update - immediately update the UI
  const optimisticLiked = !currentComment.hasLiked;
  const optimisticLikesCount = optimisticLiked ? currentComment.likes.length + 1 : currentComment.likes.length - 1;

  dispatch({
    type: COMMENT_LIKE,
    payload: {
      postid,
      commentid,
      liked: optimisticLiked,
      likesCount: optimisticLikesCount,
    },
  });

  try {
    const res = await api.post(`/post/comment/${postid}/${commentid}/like`);

    // Update with actual server response
    dispatch({
      type: COMMENT_LIKE,
      payload: {
        postid,
        commentid,
        liked: res.data.liked,
        likesCount: res.data.likesCount,
      },
    });

    return res.data.liked;
  } catch (error) {
    // Revert to previous state on error
    dispatch({
      type: COMMENT_LIKE_ERROR,
      payload: {
        postid,
        commentid,
        previousState,
      },
    });
    return null;
  }
};

// Dual Feed System Actions

export const setFeedType = (feedType) => async (dispatch, getState) => {
  try {
    // Check if the feed type is actually changing
    const currentState = getState();
    const currentFeedType = currentState.feed.feeds?.currentFeedType;

    if (currentFeedType === feedType) {
      // No change needed, just return
      return;
    }

    // Update Redux state immediately
    dispatch({
      type: SET_FEED_TYPE,
      payload: feedType,
    });

    // Save preference to server only if it actually changed
    await api.patch("/profile/feed-preference", { feedViewType: feedType });
  } catch (error) {
    console.error("Failed to save feed preference:", error);
    // Don't revert the Redux state change - user preference is still valid locally
  }
};

export const initializeFeedType = (feedViewType) => (dispatch) => {
  // Initialize feed type from user's saved preference
  dispatch({
    type: SET_FEED_TYPE,
    payload: feedViewType || "trending", // Default to 'trending' if not set
  });
};

export const getFollowingFeed = () => async (dispatch) => {
  try {
    dispatch({
      type: GET_FOLLOWING_FEED,
    });

    const state = store.getState();
    const feedState = state.feed.feeds.following;

    const res = await api.get(`/feed/posts/${feedState.offset}`);

    const newPosts = feedState.posts ? [...feedState.posts, ...res.data.posts] : res.data.posts;
    const newOffset = feedState.offset + res.data.numPosts;

    const payloadData = {
      posts: newPosts,
      offset: newOffset,
      isEndOfFeed: res.data.isEndOfFeed,
      numPosts: res.data.numPosts,
    };

    dispatch({
      type: SET_FOLLOWING_FEED,
      payload: payloadData,
    });
  } catch (error) {
    dispatch({
      type: FOLLOWING_FEED_ERROR,
    });
  }
};

export const getTrendingFeed = () => async (dispatch) => {
  try {
    dispatch({
      type: GET_TRENDING_FEED,
    });

    const state = store.getState();
    const feedState = state.feed.feeds.trending;

    const res = await api.get(`/search/foryou/${feedState.offset}`);

    // Format posts to match the expected structure (with loading and error properties)
    const formattedPosts = res.data.map((post) => ({
      ...post,
      // Ensure we have a postid field used by the feed rendering
      postid: post.postid || post.id || post._id,
      loading: true,
      error: false,
    }));

    const newPosts = feedState.posts ? [...feedState.posts, ...formattedPosts] : formattedPosts;
    const newOffset = feedState.offset + res.data.length;

    const payloadData = {
      posts: newPosts,
      offset: newOffset,
      isEndOfFeed: res.data.length < 20, // Assuming 20 posts per page
      numPosts: res.data.length,
    };

    dispatch({
      type: SET_TRENDING_FEED,
      payload: payloadData,
    });
  } catch (error) {
    dispatch({
      type: TRENDING_FEED_ERROR,
    });
  }
};

export const refreshFollowingFeed = () => async (dispatch) => {
  try {
    dispatch({
      type: REFRESH_FOLLOWING_FEED,
    });

    const res = await api.get(`/feed/posts/0`);

    const payloadData = {
      posts: res.data.posts,
      offset: res.data.numPosts,
      isEndOfFeed: res.data.isEndOfFeed,
      numPosts: res.data.numPosts,
    };

    dispatch({
      type: SET_FOLLOWING_FEED,
      payload: payloadData,
    });
  } catch (error) {
    dispatch({
      type: FOLLOWING_FEED_ERROR,
    });
  }
};

export const refreshTrendingFeed = () => async (dispatch) => {
  try {
    dispatch({
      type: REFRESH_TRENDING_FEED,
    });

    const res = await api.get(`/search/foryou/0`);

    // Format posts to match the expected structure
    const formattedPosts = res.data.map((post) => ({
      ...post,
      // Ensure we have a postid field used by the feed rendering
      postid: post.postid || post.id || post._id,
    }));

    const payloadData = {
      posts: formattedPosts,
      offset: res.data.length,
      isEndOfFeed: res.data.length < 20, // Assuming 20 posts per page
      numPosts: res.data.length,
    };

    dispatch({
      type: SET_TRENDING_FEED,
      payload: payloadData,
    });
  } catch (error) {
    dispatch({
      type: TRENDING_FEED_ERROR,
    });
  }
};
